import React, { useMemo } from 'react';
import { Box, Typography, Card, CardContent, Grid, Divider, Chip } from '@mui/material';
import { FileText } from 'lucide-react';
import { formatDateBr, formatDateTimeBr } from '../../utils/formatters/dateUtils';
import { useReportConfig } from '../../contexts/ReportConfigContext.jsx';
import ReportStyledField from './ReportStyledField.jsx';
import ReportWidget from './ReportWidget';

const formatGeneric = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const formatDocument = (value) => {
  if (!value) return '-';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return String(value);
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return String(value);
};

const getSituacaoEntidadeChipColor = (situacao) => {
  if (!situacao) return 'default';
  const normalized = String(situacao).toLowerCase();
  if (normalized.includes('regular')) return 'success';
  return 'default';
};

const getSituacaoCertidaoChipColor = (situacao) => {
  if (!situacao) return 'default';
  const normalized = String(situacao).toLowerCase();
  if (
    normalized.includes('negativa') ||
    normalized.includes('válida') ||
    normalized.includes('valida')
  ) {
    return 'success';
  }
  if (normalized.includes('vencida') || normalized.includes('expirada')) {
    return 'warning';
  }
  if (normalized.includes('positiva')) {
    return 'error';
  }
  return 'default';
};

const normalizeEntidadesData = (raw) => {
  if (!raw) return [];

  if (Array.isArray(raw)) {
    return raw.filter((item) => item && typeof item === 'object');
  }

  if (typeof raw === 'object') {
    return [raw];
  }

  return [];
};

export default function ReportPrintEntidadesDetails({ snapshot }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  if (!isDomainEnabled('entidades')) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Domínio de entidades desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  const properties =
    snapshot && snapshot.properties && typeof snapshot.properties === 'object'
      ? snapshot.properties
      : {};

  const entidadesRaw = properties.entidades;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const records = useMemo(() => normalizeEntidadesData(entidadesRaw), [entidadesRaw]);

  if (!records.length) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Nenhuma entidade encontrada para este relatório.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      {isSectionEnabled('entidades', 'resumo') && (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FileText size={20} style={{ marginRight: 8 }} />
          <Typography variant="h6">Entidades vinculadas à propriedade</Typography>
        </Box>
      )}

      {records.map((entidade, index) => {
        const situacaoObj =
          entidade && typeof entidade.situacao === 'object' ? entidade.situacao : null;
        const situacaoDescricao =
          entidade.situacao_descricao || (situacaoObj && situacaoObj.descricao) || null;
        const situacaoCodigo =
          entidade.situacao_codigo || (situacaoObj && situacaoObj.codigo) || null;

        const documento = entidade.documento || entidade.cpf || null;
        const tipoDocumento = entidade.tipo_documento;
        const sistema = entidade.sistema;
        const timestamp = entidade.timestamp;

        const certidao =
          entidade.certidao && typeof entidade.certidao === 'object' ? entidade.certidao : null;
        const certidaoResumo =
          certidao && typeof certidao.Certidao === 'object' ? certidao.Certidao : null;
        const certidaoEstruturados =
          certidao && typeof certidao.dados_estruturados === 'object'
            ? certidao.dados_estruturados
            : null;

        const certidaoTipoDocumento = certidaoEstruturados && certidaoEstruturados.tipo_documento;
        const certidaoNumeroControle = certidaoEstruturados && certidaoEstruturados.numero_controle;
        const certidaoCodigoVerificacao =
          certidaoEstruturados && certidaoEstruturados.codigo_verificacao;
        const certidaoSituacao = certidaoEstruturados && certidaoEstruturados.situacao;

        const certidaoDataEmissao =
          certidaoResumo && certidaoResumo.DataEmissao
            ? formatDateTimeBr(certidaoResumo.DataEmissao)
            : null;
        const certidaoDataValidade =
          certidaoResumo && certidaoResumo.DataValidade
            ? formatDateBr(certidaoResumo.DataValidade)
            : null;

        const certidaoContribuinte = certidaoEstruturados && certidaoEstruturados.contribuinte;
        const certidaoContribuinteNome = certidaoContribuinte && certidaoContribuinte.nome;
        const certidaoContribuinteDocumento =
          certidaoContribuinte && certidaoContribuinte.documento;

        const certidaoTextoCertificacao =
          certidaoEstruturados && certidaoEstruturados.texto_certificacao;

        return (
          <Card
            key={entidade.documento || entidade.cpf || entidade.ni || index}
            sx={{
              mb: 2,
              borderRadius: 2,
              borderLeft: 4,
              borderLeftColor: 'info.main',
              boxShadow: 1,
            }}
          >
            <CardContent>
              {isSectionEnabled('entidades', 'cabecalho_entidade') && (
                <ReportWidget sectionKey="entidades" widgetKey="entidades_cabecalho">
                  <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        {isFieldEnabled('entidades', 'cabecalho_entidade', 'nome') && (
                          <ReportStyledField
                            domainKey="entidades"
                            fieldKey="nome"
                            variant="subtitle2"
                            defaultSx={{ mb: 0.5 }}
                          >
                            {formatGeneric(entidade.nome)}
                          </ReportStyledField>
                        )}
                        <Grid container spacing={1}>
                          {isFieldEnabled('entidades', 'cabecalho_entidade', 'documento') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('entidades', 'documento', 'Documento')}
                              </Typography>
                              <ReportStyledField
                                domainKey="entidades"
                                fieldKey="documento"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatDocument(documento)}
                              </ReportStyledField>
                            </Grid>
                          )}
                          {isFieldEnabled('entidades', 'cabecalho_entidade', 'tipo_documento') && (
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('entidades', 'tipo_documento', 'Tipo de documento')}
                              </Typography>
                              <ReportStyledField
                                domainKey="entidades"
                                fieldKey="tipo_documento"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(tipoDocumento)}
                              </ReportStyledField>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: { xs: 'flex-start', md: 'flex-end' },
                            gap: 0.75,
                          }}
                        >
                          {isFieldEnabled(
                            'entidades',
                            'cabecalho_entidade',
                            'situacao_descricao',
                          ) && (
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('entidades', 'situacao_descricao', 'Situação')}
                              </Typography>
                              {situacaoDescricao ? (
                                <Chip
                                  size="small"
                                  color={getSituacaoEntidadeChipColor(situacaoDescricao)}
                                  label={situacaoDescricao}
                                  sx={{ mt: 0.25 }}
                                />
                              ) : (
                                <Typography variant="body2">-</Typography>
                              )}
                            </Box>
                          )}
                          {isFieldEnabled('entidades', 'cabecalho_entidade', 'sistema') && (
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel('entidades', 'sistema', 'Sistema')}
                              </Typography>
                              <ReportStyledField
                                domainKey="entidades"
                                fieldKey="sistema"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {formatGeneric(sistema)}
                              </ReportStyledField>
                            </Box>
                          )}
                          {isFieldEnabled(
                            'entidades',
                            'cabecalho_entidade',
                            'ultima_atualizacao',
                          ) && (
                            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel(
                                  'entidades',
                                  'ultima_atualizacao',
                                  'Última atualização',
                                )}
                              </Typography>
                              <ReportStyledField
                                domainKey="entidades"
                                fieldKey="ultima_atualizacao"
                                variant="body2"
                                defaultSx={{ fontWeight: 500 }}
                              >
                                {timestamp ? formatDateTimeBr(timestamp) : '-'}
                              </ReportStyledField>
                            </Box>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </ReportWidget>
              )}

              {isSectionEnabled('entidades', 'dados_entidade') && (
                <ReportWidget sectionKey="entidades" widgetKey="entidades_dados">
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Dados da entidade
                    </Typography>
                    <Grid container spacing={1.5}>
                      {isFieldEnabled('entidades', 'dados_entidade', 'ni') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('entidades', 'ni', 'NI')}
                          </Typography>
                          <ReportStyledField
                            domainKey="entidades"
                            fieldKey="ni"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(entidade.ni)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('entidades', 'dados_entidade', 'situacao_codigo') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('entidades', 'situacao_codigo', 'Situação (código)')}
                          </Typography>
                          <ReportStyledField
                            domainKey="entidades"
                            fieldKey="situacao_codigo"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(situacaoCodigo)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('entidades', 'dados_entidade', 'nascimento') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('entidades', 'nascimento', 'Nascimento')}
                          </Typography>
                          <ReportStyledField
                            domainKey="entidades"
                            fieldKey="nascimento"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(entidade.nascimento)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('entidades', 'dados_entidade', 'status_code') && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('entidades', 'status_code', 'Status code')}
                          </Typography>
                          <ReportStyledField
                            domainKey="entidades"
                            fieldKey="status_code"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(entidade._status_code)}
                          </ReportStyledField>
                        </Grid>
                      )}
                      {isFieldEnabled('entidades', 'dados_entidade', 'status_message') && (
                        <Grid item xs={12} sm={6} md={8}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {getFieldLabel('entidades', 'status_message', 'Status message')}
                          </Typography>
                          <ReportStyledField
                            domainKey="entidades"
                            fieldKey="status_message"
                            variant="body2"
                            defaultSx={{ fontWeight: 500 }}
                          >
                            {formatGeneric(entidade._status_message)}
                          </ReportStyledField>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </ReportWidget>
              )}

              <Divider sx={{ my: 1.5 }} />

              {certidao && isSectionEnabled('entidades', 'certidao_entidade') && (
                <ReportWidget sectionKey="entidades" widgetKey="entidades_certidao_entidade">
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Certidão vinculada à entidade
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Grid container spacing={1.5}>
                        {isFieldEnabled(
                          'entidades',
                          'certidao_entidade',
                          'certidao_tipo_documento',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'entidades',
                                'certidao_tipo_documento',
                                'Tipo de documento',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="entidades"
                              fieldKey="certidao_tipo_documento"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(certidaoTipoDocumento)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'entidades',
                          'certidao_entidade',
                          'certidao_numero_controle',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'entidades',
                                'certidao_numero_controle',
                                'Número de controle',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="entidades"
                              fieldKey="certidao_numero_controle"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(certidaoNumeroControle)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'entidades',
                          'certidao_entidade',
                          'certidao_codigo_verificacao',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'entidades',
                                'certidao_codigo_verificacao',
                                'Código de verificação',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="entidades"
                              fieldKey="certidao_codigo_verificacao"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {formatGeneric(certidaoCodigoVerificacao)}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled('entidades', 'certidao_entidade', 'certidao_situacao') && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'entidades',
                                'certidao_situacao',
                                'Situação da certidão',
                              )}
                            </Typography>
                            {certidaoSituacao ? (
                              <Chip
                                size="small"
                                color={getSituacaoCertidaoChipColor(certidaoSituacao)}
                                label={certidaoSituacao}
                                sx={{ mt: 0.25 }}
                              />
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'entidades',
                          'certidao_entidade',
                          'certidao_data_emissao',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'entidades',
                                'certidao_data_emissao',
                                'Data de emissão',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="entidades"
                              fieldKey="certidao_data_emissao"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {certidaoDataEmissao || '-'}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isFieldEnabled(
                          'entidades',
                          'certidao_entidade',
                          'certidao_data_validade',
                        ) && (
                          <Grid item xs={12} sm={6}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block' }}
                            >
                              {getFieldLabel(
                                'entidades',
                                'certidao_data_validade',
                                'Data de validade',
                              )}
                            </Typography>
                            <ReportStyledField
                              domainKey="entidades"
                              fieldKey="certidao_data_validade"
                              variant="body2"
                              defaultSx={{ fontWeight: 500 }}
                            >
                              {certidaoDataValidade || '-'}
                            </ReportStyledField>
                          </Grid>
                        )}
                        {isSectionEnabled('entidades', 'texto_certificacao') &&
                          isFieldEnabled(
                            'entidades',
                            'texto_certificacao',
                            'texto_certificacao',
                          ) && (
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block' }}
                              >
                                {getFieldLabel(
                                  'entidades',
                                  'texto_certificacao',
                                  'Texto da certificação',
                                )}
                              </Typography>
                              <ReportStyledField
                                domainKey="entidades"
                                fieldKey="texto_certificacao"
                                variant="body2"
                                defaultSx={{
                                  color: 'text.secondary',
                                  whiteSpace: 'pre-line',
                                }}
                              >
                                {certidaoTextoCertificacao || 'Não informado.'}
                              </ReportStyledField>
                            </Grid>
                          )}
                      </Grid>
                    </Box>

                    <ReportWidget
                      sectionKey="entidades"
                      widgetKey="entidades_certidao_contribuinte"
                    >
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Contribuinte da certidão
                        </Typography>
                        {isSectionEnabled('entidades', 'certidao_contribuinte') && (
                          <Grid container spacing={1.5}>
                            {isFieldEnabled(
                              'entidades',
                              'certidao_contribuinte',
                              'certidao_contribuinte_nome',
                            ) && (
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {getFieldLabel('entidades', 'certidao_contribuinte_nome', 'Nome')}
                                </Typography>
                                <ReportStyledField
                                  domainKey="entidades"
                                  fieldKey="certidao_contribuinte_nome"
                                  variant="body2"
                                  defaultSx={{ fontWeight: 500 }}
                                >
                                  {formatGeneric(certidaoContribuinteNome)}
                                </ReportStyledField>
                              </Grid>
                            )}
                            {isFieldEnabled(
                              'entidades',
                              'certidao_contribuinte',
                              'certidao_contribuinte_documento',
                            ) && (
                              <Grid item xs={12} sm={6}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {getFieldLabel(
                                    'entidades',
                                    'certidao_contribuinte_documento',
                                    'Documento',
                                  )}
                                </Typography>
                                <ReportStyledField
                                  domainKey="entidades"
                                  fieldKey="certidao_contribuinte_documento"
                                  variant="body2"
                                  defaultSx={{ fontWeight: 500 }}
                                >
                                  {formatDocument(certidaoContribuinteDocumento)}
                                </ReportStyledField>
                              </Grid>
                            )}
                          </Grid>
                        )}
                      </Box>
                    </ReportWidget>
                  </Box>
                </ReportWidget>
              )}
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );
}
